import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, Save, Send, RotateCcw, FileText, Award, History, CheckCircle2, Lock, Unlock, Undo2 } from "lucide-react";
import { gradeColorClass } from "../components/ReportSheet";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  review: { label: "Siap Diperiksa", cls: "bg-amber-100 text-amber-700" },
  revision: { label: "Perlu Revisi", cls: "bg-rose-100 text-rose-700" },
  approved: { label: "Disetujui", cls: "bg-sky-100 text-sky-700" },
  final: { label: "Final / Dikunci", cls: "bg-emerald-100 text-emerald-700" },
};

const gradeLetter = (avg, scale) => {
  if (avg == null) return "-";
  const b = scale.find((x) => avg >= x.min && avg <= x.max);
  return b ? b.grade : "-";
};

export default function ClassScores() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({}); // student_id -> {academic:{}, non_academic:{}}
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const status = data?.status || "draft";
  const role = user?.role;
  const isCoord = role === "coordinator";
  const isBM = role === "branch_manager";
  const teacherCanEdit = role === "teacher" && ["draft", "revision"].includes(status);
  const readOnly = isCoord || (role === "teacher" && !teacherCanEdit);

  const load = useCallback(() => {
    api.get(`/classes/${classId}/scores`).then((r) => {
      setData(r.data);
      const init = {};
      r.data.students.forEach((s) => {
        const sc = r.data.scores[s.id] || {};
        init[s.id] = { academic: { ...(sc.academic || {}) }, non_academic: { ...(sc.non_academic || {}) } };
      });
      setRows(init);
    }).catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat"));
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  const setAcad = (sid, key, val) => setRows((p) => ({ ...p, [sid]: { ...p[sid], academic: { ...p[sid].academic, [key]: val } } }));
  const setNA = (sid, key, val) => setRows((p) => ({ ...p, [sid]: { ...p[sid], non_academic: { ...p[sid].non_academic, [key]: val } } }));

  const rowAvg = (sid) => {
    const keys = (data?.academic_components || []).map((c) => c.key);
    const vals = keys.map((k) => rows[sid]?.academic?.[k]).filter((v) => v !== "" && v != null && !isNaN(v)).map(Number);
    if (!vals.length) return { avg: null, complete: false };
    const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    return { avg, complete: vals.length === keys.length };
  };

  const save = async () => {
    // validate 0-100
    for (const sid of Object.keys(rows)) {
      for (const k of Object.keys(rows[sid].academic)) {
        const v = rows[sid].academic[k];
        if (v !== "" && v != null && (isNaN(v) || Number(v) < 0 || Number(v) > 100)) { toast.error("Nilai harus antara 0 dan 100"); return; }
      }
    }
    setSaving(true);
    const payload = { scores: Object.keys(rows).map((sid) => ({ student_id: sid, academic: rows[sid].academic, non_academic: rows[sid].non_academic })) };
    try { await api.put(`/classes/${classId}/scores`, payload); toast.success("Nilai tersimpan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const setStatus = async (s, needNote) => {
    let note = "";
    if (needNote) { note = window.prompt("Catatan (opsional) untuk guru:") || ""; }
    try { await api.post(`/classes/${classId}/recap/status`, { status: s, note }); toast.success(`Status: ${STATUS_META[s]?.label || s}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah status"); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Memuat...</div>;
  const scale = data.grading_scale || [];

  return (
    <div className="space-y-4" data-testid="class-scores-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/classes")} className="rounded-full gap-1" data-testid="scores-back"><ArrowLeft className="w-4 h-4" /> Kembali</Button>
        <div className="flex items-center gap-2">
          <Badge className={`rounded-full ${STATUS_META[status]?.cls || "bg-slate-100"}`} data-testid="recap-status">{STATUS_META[status]?.label || status}</Badge>
          <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} className="rounded-full gap-1" data-testid="open-history"><History className="w-4 h-4" /> Riwayat</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/classes/${classId}/recap`)} className="rounded-full gap-1" data-testid="open-recap"><FileText className="w-4 h-4" /> Rekap</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/classes/${classId}/reports`)} className="rounded-full gap-1" data-testid="open-reports"><Award className="w-4 h-4" /> Rapor</Button>
        </div>
      </div>

      {status === "revision" && data.status_note && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700" data-testid="revision-note-banner">
          <b>Catatan revisi:</b> {data.status_note}
        </div>
      )}

      <Card className="rounded-2xl border-none shadow-sm p-5 bg-white">
        <h2 className="font-nunito font-extrabold text-xl text-gtsnavy">Input Nilai — {data.class.name}</h2>
        <p className="text-sm text-muted-foreground">{data.class.school_name} · Guru {data.class.teacher_name} · Sem {data.class.semester} · {data.class.academic_year}</p>
      </Card>

      <Card className="rounded-2xl border-none shadow-sm bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="scores-table">
          <thead className="bg-gtsnavy/5 text-gtsnavy">
            <tr>
              <th className="px-2 py-3 text-left w-10 sticky left-0 bg-[#eef1f6]">No</th>
              <th className="px-2 py-3 text-left sticky left-10 bg-[#eef1f6] min-w-[160px]">Nama Siswa</th>
              {data.academic_components.map((c) => <th key={c.key} className="px-2 py-3 text-center min-w-[80px]">{c.label}</th>)}
              <th className="px-2 py-3 text-center bg-gtsorange/10">Angka</th>
              <th className="px-2 py-3 text-center bg-gtsorange/10">Huruf</th>
              {data.non_academic_components.map((c) => <th key={c.key} className="px-2 py-3 text-center">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.students.length === 0 ? (
              <tr><td colSpan={4 + data.academic_components.length + data.non_academic_components.length} className="px-3 py-10 text-center text-muted-foreground" data-testid="scores-empty">Belum ada siswa. Tambahkan siswa dulu di menu Siswa.</td></tr>
            ) : data.students.map((s) => {
              const { avg, complete } = rowAvg(s.id);
              return (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`score-row-${s.id}`}>
                  <td className="px-2 py-1.5 sticky left-0 bg-white">{s.number}</td>
                  <td className="px-2 py-1.5 font-medium text-gtsnavy sticky left-10 bg-white">{s.name}</td>
                  {data.academic_components.map((c) => (
                    <td key={c.key} className="px-1 py-1.5 text-center">
                      <input type="number" min="0" max="100" disabled={readOnly}
                        value={rows[s.id]?.academic?.[c.key] ?? ""}
                        onChange={(e) => setAcad(s.id, c.key, e.target.value === "" ? "" : e.target.value)}
                        className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-center focus:border-gtsorange focus:outline-none disabled:bg-slate-50"
                        data-testid={`score-${s.id}-${c.key}`} />
                    </td>
                  ))}
                  <td className={`px-2 py-1.5 text-center font-bold ${complete ? "text-gtsnavy" : "text-amber-500"}`} data-testid={`avg-${s.id}`}>{avg ?? "–"}</td>
                  <td className={`px-2 py-1.5 text-center font-bold ${gradeColorClass(gradeLetter(avg, scale))}`} data-testid={`letter-${s.id}`}>{gradeLetter(avg, scale)}</td>
                  {data.non_academic_components.map((c) => (
                    <td key={c.key} className="px-1 py-1.5 text-center">
                      <select disabled={readOnly} value={rows[s.id]?.non_academic?.[c.key] ?? ""}
                        onChange={(e) => setNA(s.id, c.key, e.target.value)}
                        className="rounded-lg border border-slate-200 px-2 py-1 focus:border-gtsorange focus:outline-none disabled:bg-slate-50"
                        data-testid={`na-${s.id}-${c.key}`}>
                        <option value="">-</option><option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
                      </select>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {data.students.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="status-actions">
          {(role === "teacher" && teacherCanEdit) || isBM ? <Button onClick={save} disabled={saving} className="rounded-full bg-gtsnavy text-white gap-1" data-testid="save-scores-btn"><Save className="w-4 h-4" /> Simpan Semua</Button> : null}

          {role === "teacher" && ["draft", "revision"].includes(status) && <Button onClick={() => setStatus("review")} variant="outline" className="rounded-full gap-1" data-testid="submit-review-btn"><Send className="w-4 h-4" /> Kirim untuk Diperiksa</Button>}
          {role === "teacher" && status === "review" && <Button onClick={() => setStatus("draft")} variant="outline" className="rounded-full gap-1" data-testid="back-draft-btn"><RotateCcw className="w-4 h-4" /> Kembalikan ke Draft</Button>}

          {isCoord && ["review", "approved"].includes(status) && <Button onClick={() => setStatus("revision", true)} variant="outline" className="rounded-full gap-1 text-rose-600 border-rose-200" data-testid="return-revision-btn"><Undo2 className="w-4 h-4" /> Kembalikan untuk Revisi</Button>}
          {isCoord && status === "review" && <Button onClick={() => setStatus("approved")} variant="outline" className="rounded-full gap-1 text-sky-700 border-sky-200" data-testid="mark-checked-btn"><CheckCircle2 className="w-4 h-4" /> Tandai Diperiksa</Button>}

          {isBM && status !== "final" && <Button onClick={() => setStatus("revision", true)} variant="outline" className="rounded-full gap-1 text-rose-600 border-rose-200" data-testid="bm-revision-btn"><Undo2 className="w-4 h-4" /> Minta Revisi</Button>}
          {isBM && ["review", "approved", "draft", "revision"].includes(status) && status !== "approved" && <Button onClick={() => setStatus("approved")} variant="outline" className="rounded-full gap-1 text-sky-700 border-sky-200" data-testid="bm-approve-btn"><CheckCircle2 className="w-4 h-4" /> Setujui</Button>}
          {isBM && status !== "final" && <Button onClick={() => { if (window.confirm("Kunci laporan sebagai Final? Guru tidak dapat mengubah lagi.")) setStatus("final"); }} className="rounded-full bg-emerald-600 text-white gap-1" data-testid="bm-final-btn"><Lock className="w-4 h-4" /> Kunci Final</Button>}
          {isBM && status === "final" && <Button onClick={() => { if (window.confirm("Buka kembali laporan Final? Status akan menjadi Disetujui.")) setStatus("approved"); }} variant="outline" className="rounded-full gap-1" data-testid="bm-reopen-btn"><Unlock className="w-4 h-4" /> Buka Kembali</Button>}
        </div>
      )}
      {role === "teacher" && !teacherCanEdit && <p className="text-sm text-amber-600" data-testid="locked-note">Laporan berstatus "{STATUS_META[status]?.label}". {status === "review" ? "Klik \"Kembalikan ke Draft\" untuk mengedit." : "Menunggu tindakan Coordinator/Branch Manager."}</p>}

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Riwayat Perubahan Status</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="history-list">
            {(data.history || []).length === 0 ? <p className="text-sm text-muted-foreground">Belum ada riwayat.</p> :
              [...data.history].reverse().map((h, i) => (
                <div key={i} className="rounded-xl border border-slate-100 p-3 text-sm" data-testid={`history-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <span><Badge className={`rounded-full mr-1 ${STATUS_META[h.to]?.cls}`}>{STATUS_META[h.to]?.label || h.to}</Badge></span>
                    <span className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString("id-ID")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">oleh {h.by} ({h.role})</p>
                  {h.note && <p className="text-xs mt-1"><b>Catatan:</b> {h.note}</p>}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
